import axios from "axios";
import { notifyError } from "common/utils/app-monitoring/notify";
import { dropEmptyKeys } from "../core";
import { isSessionExpired, refreshCsrfToken } from "../csrf-token-manager";
import getMetaEnv from "../meta";
import { fetchWithRetry } from "../retries";
import { withCache } from "./cache";

const withCsrfRetry = async (requestFn, source) => {
  try {
    return await requestFn();
  } catch (error) {
    if (!isSessionExpired(error)) throw error;

    const refreshed = await refreshCsrfToken(source);
    if (!refreshed) {
      throw error;
    }

    return requestFn();
  }
};

const MUTATION_METHODS = ["POST", "PUT", "PATCH", "DELETE"];

const isNetworkError = error =>
  error.message === "Load failed" ||
  error.message === "Failed to fetch" ||
  error.message === "Unable to connect to server";

const defaultHeaders = () => ({
  "X-CSRF-Token": getMetaEnv("csrf-token"),
  "X-Requested-With": "XMLHttpRequest",
  Accept: "application/json",
  "Content-Type": "application/json; charset=utf-8"
});

const buildParams = ({ headers = {}, ...rest }, method) => ({
  method,
  headers: dropEmptyKeys({
    ...defaultHeaders(),
    ...headers
  }),
  credentials: "same-origin",
  ...rest
});

const haltErrorResponse = async response => {
  if (response.status >= 400 && response.status <= 599) {
    let errorMessage;
    let responseData;
    const textBody = await response.text();

    // Try to parse JSON response body
    try {
      responseData = JSON.parse(textBody);
    } catch {
      responseData = textBody;
    }

    if (textBody) {
      errorMessage = response.status === 422 ? textBody : `${response.statusText}: ${textBody}`;
    } else {
      errorMessage = response.statusText;
    }
    const error = new Error(errorMessage);
    error.status = response.status;
    error.message = errorMessage;
    error.response = responseData; // Preserve the parsed response data
    throw error;
  }
  return response;
};

const increaseAsyncRequestsCount = () => {
  window.ajax_active += 1;
};

const decreaseAsyncRequestsCount = () => {
  window.ajax_active -= 1;
};

const sendBeacon = (url, data) =>
  new Promise((resolve, reject) => {
    const dataString = JSON.stringify(data);
    const queued = navigator.sendBeacon(url, dataString);

    if (queued) {
      resolve();
    } else {
      reject(new Error(`Failed to queue data for sending to ${url}`));
    }
  });

export const shouldReportError = error => {
  if (!error) return true;

  // fetch errors (499 and abort)
  if (error.status === 499 || error.name === "AbortError") return false;

  // axios errors (http + cancel)
  if (axios.isAxiosError?.(error)) {
    if (error.response?.status === 499) return false;
    if (error.code === "ERR_CANCELED" || error.message === "canceled") return false;

    // legacy cancel token / compat
    if (axios.isCancel?.(error)) return false;
  }

  // fallback for existing checks / non-axios cancel shapes
  if (error.name === "CanceledError") return false;
  if (error.message === "Client Closed Request") return false;
  if (isNetworkError(error)) return false;

  return true;
};

const executeFetch = (url, params, method) =>
  fetchWithRetry(url, buildParams(params, method))
    .then(haltErrorResponse)
    .then(async response => {
      if (response.status === 204) return undefined;
      const text = await response.text();
      return text ? JSON.parse(text) : undefined;
    });

const request = (url, params = {}, method = "GET") => {
  increaseAsyncRequestsCount();

  if (params.sendBeacon) {
    // eslint-disable-next-line no-param-reassign
    delete params.sendBeacon;
    decreaseAsyncRequestsCount();
    return sendBeacon(url, params);
  }

  const fetchPromise = MUTATION_METHODS.includes(method.toUpperCase())
    ? withCsrfRetry(() => executeFetch(url, params, method), "request_layer")
    : executeFetch(url, params, method);

  return fetchPromise
    .catch(error => {
      try {
        if (shouldReportError(error)) {
          notifyError(error);
        }
      } catch (err) {
        notifyError("Error has malformed data");
      }
      // Throw a common error for network errors
      if (isNetworkError(error)) {
        throw new Error("Unable to connect to server");
      }
      throw error;
    })
    .finally(decreaseAsyncRequestsCount);
};

/**
 * A non-native implementation of request method
 * You should use default export unless you need specific functions like progress tracking
 * @param url
 * @param params
 * @param method
 * @returns {Promise<void>|Promise<unknown>}
 */
const executeAxios = (url, params, method) =>
  axios(url, buildParams(params, method))
    .then(haltErrorResponse)
    .then(response => response.data);

export const axiosRequest = (url, params = {}, method = "GET") => {
  increaseAsyncRequestsCount();

  if (params.sendBeacon) {
    // eslint-disable-next-line no-param-reassign
    delete params.sendBeacon;
    decreaseAsyncRequestsCount();
    return sendBeacon(url, params);
  }

  const axiosPromise = MUTATION_METHODS.includes(method.toUpperCase())
    ? withCsrfRetry(() => executeAxios(url, params, method), "axios_request_layer")
    : executeAxios(url, params, method);

  return axiosPromise
    .catch(error => {
      try {
        if (shouldReportError(error)) {
          notifyError(error);
        }
      } catch (err) {
        notifyError("Error has malformed data");
      }
      throw error;
    })
    .finally(decreaseAsyncRequestsCount);
};

export default withCache(request);
